import { FormEvent, useEffect, useState } from 'react';
import classnames from 'classnames';
import { Form, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { makeStyles, Button as MuiButton } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import jwt from 'jsonwebtoken';
import ReactLoading from 'react-loading';

import { addFlashMessage } from 'store/reducers/flashMessages';
import { useLoginMutation, useResetPasswordMutation, useSignInAzureUserWithCodeMutation } from 'utils/wemble-api';
import { useAppDispatch, useAppSelector } from 'store';
import { useSetAuth } from 'hooks';
import Redirect from 'components/Redirect';
import getOrganizationSettings, { getDomainSsoConfig } from 'utils/organization';

import wembleIcon from '../../utils/logo.webp';
import './css/material-design-iconic-font.min.css';
import './fonts/font-awesome-4.7.0/css/font-awesome.min.css';
import './css/util.css';
import './css/main.css';
import { ReactComponent as MSLogo } from 'assets/icons/ms-logo.svg';
import { useTeamsAuth } from 'hooks/useTeamsAuth';

const useStyles = makeStyles(() => ({
  root: {
    backgroundImage: 'url("background.png")',
    height: '100%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  logo: {
    pointerEvents: 'none',
    width: '60%',
    marginTop: -30,
    marginBottom: 35,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  loader: {
    fill: '#59abcb !important',
    margin: 'auto',
    marginTop: 50,
    marginBottom: 50,
    width: '10%',
  },
  ssoButton: {
    width: '100%',
    backgroundColor: '#f0f0f0',
    padding: '10px 16px',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
}));

const LoginPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { search, pathname } = useLocation();

  const dispatch = useAppDispatch();
  const setAuth = useSetAuth();
  const { initialized: teamsInitialized, error: teamsAuthError } = useTeamsAuth();
  const token = new URLSearchParams(useLocation().search).get('token');
  const authenticated = useAppSelector((state) => state.auth.authenticated);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forcePasswordMode, setForcePasswordMode] = useState(false);
  const [loginUser] = useLoginMutation();
  const [resetPassword] = useResetPasswordMutation();

  const [loginAzureUser, { isLoading: isLoggingInAzureUser }] = useSignInAzureUserWithCodeMutation();
  const azureRedirectUri = `${window.location.origin}/login`;

  // Detect native iOS app with MSAL support (v5+)
  const iosAppVersionMatch = /Wemble\(iOS\)\s+([\d.]+)/.exec(navigator.userAgent);
  const supportsNativeSso = iosAppVersionMatch ? parseFloat(iosAppVersionMatch[1]) >= 5 : false;

  const handleNativeSsoLogin = () => {
    (window as any).webkit?.messageHandlers?.startSsoLogin?.postMessage('');
  };

  useEffect(() => {
    if (teamsAuthError) {
      // @ts-ignore
      setError('server-login', { message: teamsAuthError });
    }
  }, [teamsAuthError]);

  useEffect(() => {
    if (token) {
      const user = jwt.decode(token);
      if (!user) return;
      if (user.completed) {
        setAuth(token);
        history.push('/');
      } else {
        history.push(`/signup/${user.signupToken}?sso=true`);
      }
    }
  }, [token, history, setAuth]);

  const { register, watch, handleSubmit, setError, clearErrors, reset, setValue, formState } = useForm({
    defaultValues: {
      email: '',
      password: '',
      resetEmail: '',
    },
  });
  const { errors, isSubmitting } = formState;
  const fields = watch();

  const disabled = isSubmitting;

  useEffect(() => {
    const searchObject = Object.fromEntries(
      search
        .substring(1)
        .split('&')
        .map((v) => v.split('=')),
    );
    if (searchObject['code']) {
      loginAzureUser({
        body: {
          code: searchObject['code'],
          redirectUri: searchObject['azureRedirectUri'] ? decodeURIComponent(searchObject['azureRedirectUri']) : azureRedirectUri,
        },
      })
        .unwrap()
        .then((res) => {
          const user = jwt.decode(res.token);
          if (!user) return;
          if (user.completed) {
            setAuth(res.token);
            history.push('/');
          } else {
            history.push(`/signup/${user.signupToken}?sso=true`);
          }
        })
        .catch((error) => {
          if (error?.data?.redirect) {
            window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${process.env.REACT_APP_AAD_APP_CLIENT_ID}&scope=https://graph.microsoft.com/.default&response_type=code&redirect_uri=${error?.data?.redirect}&prompt=${(azureRedirectUri.includes("crowell") || azureRedirectUri.includes("faber")) ? "select_account" : ("none&login_hint=" + error?.data?.email)}`
          }
          else {
            // @ts-ignore
            setError('server-login', { message: error?.data?.message });
            history.replace(pathname);
          }
        });
    }
    if (searchObject["email"]) {
      setValue("email", searchObject["email"])
      history.replace(pathname);
    }
  }, [search, pathname, history]);

  if (authenticated) {
    return <Redirect />;
  }

  const onSubmit = (event: FormEvent) => {
    clearErrors();
    handleSubmit(async (data) => {
      clearErrors();
      const res = await loginUser({
        loginParams: {
          email: data.email,
          password: data.password,
        },
      });
      if ('error' in res) {
        const error = res.error as any;
        if (typeof error?.data?.message === 'string') {
          // @ts-ignore
          setError('server-login', { message: error?.data?.message });
        }
        return;
      }
      const user = jwt.decode(res.data.token);
      if (user.completed) {
        setAuth(res.data.token);
        history.push('/');
      } else {
        history.push(`/signup/${user.id}?sso=true`);
      }
    })(event);
  };

  const onForgotPasswordSubmit = (event: FormEvent) => {
    clearErrors();
    handleSubmit(async (data) => {
      clearErrors();
      const res = await resetPassword({
        resetPasswordParams: {
          resetEmail: data.resetEmail,
        },
      });
      if ('error' in res) {
        const error = res.error as any;
        if (typeof error?.data?.message === 'string') {
          // @ts-ignore
          setError('server-forgot-password', { message: error?.data?.message });
        }
        return;
      }
      dispatch(addFlashMessage({ type: 'success', text: 'Reset link sent' }));
      reset();
      setShowForgotPasswordModal(false);
    })(event);
  };

  const handleToggleForgotPasswordModal = () => {
    setShowForgotPasswordModal((prev) => {
      if (prev) {
        setValue('resetEmail', '');
        // @ts-ignore
        setError('server-forgot-password', '');
      } else {
        clearErrors();
      }
      return !prev;
    });
  };

  let redirectMessage: string | null = null;
  let url: string | null = null;
  let isInstallationRedirect = false;
  let shouldShowMicrosoftButton = false;

  // Skip SSO-forcing and installation redirects in local dev — Azure AD won't accept a
  // localhost redirect_uri, and installation redirects would bounce away from the dev server
  // (which may be pointed at any tenant's DB via MONGO_URL).
  const isDevEnv = process.env.NODE_ENV === 'development';

  const domainConfig = getDomainSsoConfig(fields.email);
  const forceSso = (domainConfig?.forceSso ?? false) && !isDevEnv;

  // Default SSO view: no email typed and not in password mode
  const showDefaultSso = !forcePasswordMode && !fields.email.trim();

  // Check installation redirects first (always happens, takes priority)
  if (!isDevEnv && domainConfig?.installation && !window.location.hostname.includes(domainConfig.installation)) {
    const orgSettings = getOrganizationSettings(domainConfig.companyId);
    redirectMessage = `${orgSettings.name} installation`;
    url = `https://${domainConfig.installation}/login?email=${encodeURI(fields.email)}`;
    isInstallationRedirect = true;
  }
  else if (!isDevEnv && window.location.hostname.includes("crowell.wemble.app") && (new RegExp('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')).test(fields.email) && !domainConfig?.installation?.includes('crowell') && !fields.email.includes('@getwemble')) {
    redirectMessage = 'Wemble EU installation';
    url = `https://www.wemble.app/login?email=${encodeURI(fields.email)}`;
    isInstallationRedirect = true;
  }
  // SSO domain detected (forceSso overrides password mode)
  else if ((!forcePasswordMode || forceSso) && domainConfig?.ssoMethod === 'entra') {
    shouldShowMicrosoftButton = true;
  }

  // Default SSO view (no email typed yet)
  if (showDefaultSso) {
    shouldShowMicrosoftButton = true;
  }

  // SSO nudge: user is in password mode but typed an SSO domain — nudge them toward SSO
  const showSsoNudge = forcePasswordMode && !forceSso && domainConfig?.ssoMethod === 'entra' && !redirectMessage;

  return (
    <div className={classes.root}>
      <div className="limiter">
        <div className="container-login100">
          <div className="wrap-login100">
            {!teamsInitialized || isLoggingInAzureUser ? (
              <ReactLoading type={'spinningBubbles'} className={classes.loader} />
            ) : (
              <form onSubmit={onSubmit} className="login100-form validate-form">
                <img src={wembleIcon} alt="" className={classes.logo} />

                {!showDefaultSso && (
                  <div
                    className={'wrap-input100 validate-input' + (errors.email ? ' alert-validate' : '')}
                    data-validate={errors.email?.message}
                  >
                    <input
                      className={'input input100' + (fields.email ? ' has-val' : '')}
                      {...register('email')}
                      type="email"
                      required={!showDefaultSso}
                      autoComplete="email"
                    />
                    <span className="focus-input100" data-placeholder="Email" />
                  </div>
                )}

                {!shouldShowMicrosoftButton && (
                  <div
                    className="text-center"
                    style={{
                      marginTop: -32,
                      marginBottom: 8,
                      opacity: redirectMessage ? 1.0 : 0,
                      transition: '0.5s ease-in-out',
                    }}
                  >
                    <a
                      className="txt2"
                      style={{
                        fontSize: 13 + 'px',
                        color: '#0084c4',
                      }}
                      href={url ?? '#'}
                    >
                      Login through {redirectMessage}
                    </a>
                    {redirectMessage && !isInstallationRedirect && !forceSso && (
                      <div style={{ marginTop: 16 }}>
                        <button
                          type="button"
                          onClick={() => setForcePasswordMode(true)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#999',
                            fontSize: 12 + 'px',
                            cursor: 'pointer',
                            textDecoration: 'none',
                          }}
                        >
                          Use password instead
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {shouldShowMicrosoftButton ? (
                  <div className="container-login100-form-btn" style={{ marginTop: -20 }}>
                    {process.env.REACT_APP_AAD_APP_CLIENT_ID ? (
                      <MuiButton
                        startIcon={<MSLogo />}
                        {...(supportsNativeSso
                          ? { onClick: handleNativeSsoLogin }
                          : { href: `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${process.env.REACT_APP_AAD_APP_CLIENT_ID}&scope=https://graph.microsoft.com/.default&response_type=code&redirect_uri=${azureRedirectUri}&prompt=select_account` }
                        )}
                        className={classes.ssoButton}
                      >
                        Sign in with SSO
                      </MuiButton>
                    ) : null}
                    {!forceSso && (
                      <div className="text-center" style={{ marginTop: 12 }}>
                        <button
                          type="button"
                          onClick={() => setForcePasswordMode(true)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#999',
                            fontSize: 12 + 'px',
                            cursor: 'pointer',
                            textDecoration: 'none',
                          }}
                        >
                          Use password instead
                        </button>
                      </div>
                    )}
                  </div>
                ) : (!redirectMessage && (<div>
                  <div
                    className={'wrap-input100 validate-input' + (errors.password ? ' alert-validate' : '')}
                    data-validate={errors.password?.message}
                  >
                    <span
                      className={'btn-show-pass' + (showPassword ? ' active' : '')}
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      <i className="zmdi zmdi-eye" />
                    </span>
                    <input
                      className={'input input100' + (fields.password ? ' has-val' : '')}
                      type={showPassword ? 'text' : 'password'}
                      {...register('password')}
                      required
                      autoComplete="current-password"
                    />
                    <span className="focus-input100" data-placeholder="Password" />
                  </div>

                  <div className="text-center" style={{ marginTop: -24, marginBottom: 16 }}>
                    <span
                      style={{
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                        color: '#aaa',
                        fontSize: 13,
                      }}
                      onClick={handleToggleForgotPasswordModal}
                    >
                      Forgot password?
                    </span>
                  </div>

                  {errors['server-login']?.message && (
                    <span className="text-danger" style={{ fontSize: 14 }}>
                      {errors['server-login'].message}
                    </span>
                  )}

                  <div className="container-login100-form-btn">
                    <div className="wrap-login100-form-btn">
                      <div className="login100-form-bgbtn" />
                      <button type="submit" value="Submit" className="login100-form-btn">
                        Login
                      </button>
                    </div>

                    {showSsoNudge && process.env.REACT_APP_AAD_APP_CLIENT_ID && (
                      <>
                        <p style={{ color: '#666', fontSize: 13, marginTop: 16, marginBottom: 8, textAlign: 'center' }}>
                          Your organization supports SSO
                        </p>
                        <MuiButton
                          startIcon={<MSLogo />}
                          {...(supportsNativeSso
                            ? { onClick: handleNativeSsoLogin }
                            : { href: `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${process.env.REACT_APP_AAD_APP_CLIENT_ID}&scope=https://graph.microsoft.com/.default&response_type=code&redirect_uri=${azureRedirectUri}&prompt=select_account` }
                          )}
                          className={classes.ssoButton}
                        >
                          Sign in with SSO
                        </MuiButton>
                      </>
                    )}
                  </div>
                </div>))}
              </form>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={showForgotPasswordModal} toggle={handleToggleForgotPasswordModal}>
        <ModalHeader toggle={handleToggleForgotPasswordModal}>Forgot password</ModalHeader>
        <Form onSubmit={onForgotPasswordSubmit}>
          <ModalBody>
            <div className="form-group">
              <label
                style={{ fontWeight: 'bold' }}
                className={classnames('control-label', {
                  'text-danger': errors.resetEmail,
                })}
              >
                Email
              </label>
              <input
                type="email"
                {...register('resetEmail')}
                className={classnames('form-control', { 'is-invalid': errors.resetEmail })}
                autoComplete="email"
              />
              {errors.resetEmail && <span className="text-danger">{errors.resetEmail?.message}</span>}
            </div>
            {errors['server-forgot-password']?.message && (
              <span className="text-danger">{errors['server-forgot-password'].message}</span>
            )}
          </ModalBody>
          <ModalFooter>
            <Button disabled={disabled} color="secondary" onClick={handleToggleForgotPasswordModal}>
              Cancel
            </Button>{' '}
            <Button disabled={disabled} color="primary">
              Send reset link
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default LoginPage;
